import { inject, Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { ApiFragment, DomainsDataService, Family, UserService } from '@stylehaven/components';
import { saveAs } from 'file-saver';
import { Mime } from 'mime';
import otherTypes from 'mime/types/other.js';
import standardTypes from 'mime/types/standard.js';
import { map, take } from 'rxjs/operators';
import { ReplaceUnsupportedCharacters } from '../exports/replace-unsupported-characters.helper';

@Injectable({ providedIn: 'root' })
export class DownloadsService {
    private downloadName: string;
    private mime: Mime;

    // Injectors
    private readonly http: HttpClient = inject(HttpClient);
    private readonly userService: UserService = inject(UserService);
    private domainService: DomainsDataService = inject(DomainsDataService);

    constructor() {
        this.mime = new Mime(standardTypes, otherTypes); // Modifying default mime is not allowed, so we mimic it
        this.mime.define({ 'image/jpeg': ['jpg'] }, true); // Override jpg mapping to use jpg instead of jpeg
    }

    public async downloadFragmentPreview(record: ApiFragment): Promise<void> {
        const organisationId = await this.userService
            .fetchUser()
            .pipe(
                map(user => user.Organisation.Id),
                take(1)
            )
            .toPromise();

        const domain = await this.domainService.getPublicOrganisationDomain(organisationId).toPromise();

        let url: string = `https://${domain}/representations/${record.getRecordId()}?type=Access`;

        const blob: Blob = await this.http.get<Blob>(url, { responseType: 'blob' as 'json' }).toPromise();
        this.downloadName = this.getDownloadTitle(record, blob.type);
        return saveAs(blob, this.downloadName);
    }

    public downloadUrl(url: string) {
        const a: HTMLAnchorElement = document.createElement('a');
        a.href = url;
        a.download = this.downloadName;
        a.click();
    }

    private getDownloadTitle(record: ApiFragment, mimeType: string): string {
        const originalFilename: string = record.get(Family.Descriptive, 'OriginalFilename');
        const cleanFilename: string = ReplaceUnsupportedCharacters(originalFilename);
        const filenameWithoutExtension: string = cleanFilename.replace(/\.[^/.]+$/, '');
        const extension: string | null = this.mime.getExtension(mimeType);

        return `${filenameWithoutExtension}.${extension}`;
    }
}
